import { useEffect, useState, useRef } from 'react';
import { useLayoutEffect } from 'react';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useSucursal } from '../../utils/sucursalContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { applyBrandingTheme } from '../../lib/brandingTheme';

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Notificaciones reales: se cargan desde Supabase (ver estado dentro del componente).

// Feature flags (admin portal). Keep disabled until SMS/WhatsApp is implemented end-to-end.
const ENABLE_SMS_WHATSAPP_SETTINGS = false;
// Keep hidden until we implement them end-to-end.
const ENABLE_REGISTRO_ACTIVIDAD_SETTINGS = false;
const ENABLE_APIS_EXTERNAS_SETTINGS = false;
const ENABLE_RESPALDOS_SETTINGS = false;
const MOWE_DEFAULT_PRIMARY = '#314a25';
const MOWE_DEFAULT_SECONDARY = '#bfa98d';

const configuracionOpciones = [
  {
    categoria: 'General',
    items: [
      { id: 'datos-gimnasio', icon: 'ri-building-line', titulo: 'Datos del Gimnasio', descripcion: 'Nombre, dirección, teléfono y logo' },
      { id: 'personalizacion', icon: 'ri-palette-line', titulo: 'Personalización', descripcion: 'Colores, tema y apariencia de la app' },
      { id: 'idioma-region', icon: 'ri-global-line', titulo: 'Idioma y Región', descripcion: 'Zona horaria, formato de fecha y moneda' }
    ]
  },
    {
      categoria: 'Notificaciones',
      items: [
        { id: 'alertas-push', icon: 'ri-notification-badge-line', titulo: 'Alertas Push', descripcion: 'Configurar notificaciones a usuarios' },
        { id: 'correos-automaticos', icon: 'ri-mail-settings-line', titulo: 'Correos Automáticos', descripcion: 'Plantillas de email y recordatorios' },
        ...(ENABLE_SMS_WHATSAPP_SETTINGS
          ? [{ id: 'sms-whatsapp', icon: 'ri-message-2-line', titulo: 'SMS y WhatsApp', descripcion: 'Mensajes de confirmación y avisos' }]
          : []),
      ]
    },
  {
    categoria: 'Pagos',
    items: [
      { id: 'metodos-pago', icon: 'ri-bank-card-line', titulo: 'Métodos de Pago', descripcion: 'Tarjetas, transferencias y efectivo' },
      { id: 'comisiones', icon: 'ri-percent-line', titulo: 'Comisiones', descripcion: 'Configurar tasas y recargos' },
    ]
  },
  {
    categoria: 'Seguridad',
    items: [
      { id: 'usuarios-admin', icon: 'ri-shield-user-line', titulo: 'Usuarios Admin', descripcion: 'Gestionar accesos del personal' },
      { id: 'contrasenas', icon: 'ri-lock-password-line', titulo: 'Contraseñas', descripcion: 'Políticas de seguridad' },
      ...(ENABLE_REGISTRO_ACTIVIDAD_SETTINGS
        ? [{ id: 'registro-actividad', icon: 'ri-history-line', titulo: 'Registro de Actividad', descripcion: 'Historial de acciones del sistema' }]
        : [])
    ]
  },
  {
    categoria: 'Integraciones',
    items: [
      { id: 'control-acceso', icon: 'ri-qr-code-line', titulo: 'Control de Acceso', descripcion: 'QR, torniquetes y lectores' },
      ...(ENABLE_APIS_EXTERNAS_SETTINGS
        ? [{ id: 'apis-externas', icon: 'ri-apps-line', titulo: 'APIs Externas', descripcion: 'Conexiones con otros servicios' }]
        : []),
      ...(ENABLE_RESPALDOS_SETTINGS
        ? [{ id: 'respaldos', icon: 'ri-database-2-line', titulo: 'Respaldos', descripcion: 'Backup automático de datos' }]
        : [])
    ]
  }
];

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'] as const;
type DiaSemana = (typeof DIAS_SEMANA)[number];
type HorarioDia = { apertura: string; cierre: string; cerrado: boolean };
type HorariosSucursal = Record<DiaSemana, HorarioDia>;
type SucursalForm = {
  nombre: string;
  estado: 'activo' | 'mantenimiento' | 'inactivo';
  direccion: string;
  telefono: string;
  email: string;
  capacidad_maxima: string;
  horarios: HorariosSucursal;
};

type PagoProviderSecretsState = {
  commerceCode: string;
  apiKeySecret: string;
  configured: boolean;
  maskedCommerceCode: string;
  maskedApiKeySecret: string;
  loading: boolean;
  error: string | null;
};

const buildDefaultHorarios = (): HorariosSucursal =>
  Object.fromEntries(
    DIAS_SEMANA.map((dia) => [dia, { apertura: '06:00', cierre: '22:00', cerrado: false }])
  ) as HorariosSucursal;

const buildSucursalForm = (sucursal?: any): SucursalForm => {
  const baseHorarios = buildDefaultHorarios();
  const raw = (sucursal?.horarios as Record<string, HorarioDia> | undefined) || {};

  for (const dia of DIAS_SEMANA) {
    if (raw[dia]) {
      baseHorarios[dia] = {
        apertura: raw[dia].apertura || '06:00',
        cierre: raw[dia].cierre || '22:00',
        cerrado: !!raw[dia].cerrado,
      };
    }
  }

  return {
    nombre: sucursal?.nombre || '',
    estado: (sucursal?.estado || 'activo') as 'activo' | 'mantenimiento' | 'inactivo',
    direccion: sucursal?.direccion || '',
    telefono: sucursal?.telefono || '',
    email: sucursal?.email || '',
    capacidad_maxima: String(sucursal?.capacidad_maxima ?? 150),
    horarios: baseHorarios,
  };
};

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { sucursalActiva, sucursales: sucursalesDB, cambiarSucursal, refreshSucursales, loading: loadingSucursales } = useSucursal();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeConfigModal, setActiveConfigModal] = useState<string | null>(null);
  const [showSucursalSelector, setShowSucursalSelector] = useState(false);
  const [showSucursalesModal, setShowSucursalesModal] = useState(false);
  const [showEditSucursalModal, setShowEditSucursalModal] = useState(false);
  const [showNewSucursalModal, setShowNewSucursalModal] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState<any>(null);
  const [sucursalForm, setSucursalForm] = useState<SucursalForm>(() => buildSucursalForm());
  const [showSidebarUserMenu, setShowSidebarUserMenu] = useState(false);
  const [deletingSucursalId, setDeletingSucursalId] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const location = useLocation();

  type NotificacionRow = {
    id: string;
    gimnasio_id: string;
    usuario_id: string | null;
    destino: 'admin' | 'usuario';
    tipo: string;
    titulo: string;
    mensaje: string;
    meta: any;
    leida: boolean;
    created_at: string;
  };

  type NotificacionUI = {
    id: string;
    tipo: string;
    titulo: string;
    mensaje: string;
    tiempo: string;
    leida: boolean;
    created_at: string;
  };

  const [notificaciones, setNotificaciones] = useState<NotificacionUI[]>([]);
  const [notificacionesLoading, setNotificacionesLoading] = useState(false);
  const [notificacionesError, setNotificacionesError] = useState<string | null>(null);
  const unreadCount = useMemo(() => notificaciones.filter((n) => !n.leida).length, [notificaciones]);

  // Estados para configuración
  const [configData, setConfigData] = useState({
    // Datos del gimnasio
    nombreGimnasio: 'PowerFit Gym',
    direccion: 'Av. Principal 123, Santiago',
    telefono: '+56 9 8765 4321',
    email: 'info@powerfit.cl',
    sitioWeb: 'www.powerfit.cl',
    
    // Personalización
    colorPrimario: MOWE_DEFAULT_PRIMARY,
    colorSecundario: MOWE_DEFAULT_SECONDARY,
    logoUrl: '',
    temaOscuro: false,
    
    // Idioma y región
    idioma: 'es',
    zonaHoraria: 'America/Santiago',
    moneda: 'CLP',
    formatoFecha: 'dd/MM/yyyy',
    
    // Notificaciones
    pushReservas: true,
    pushPagos: true,
    pushCancelaciones: true,
    emailBienvenida: true,
    emailRecordatorios: true,
    emailVencimientos: true,
    smsConfirmaciones: false,
    whatsappAvisos: false,
    
    // Pagos
    efectivo: true,
    tarjetaCredito: true,
    tarjetaDebito: true,
    transferencia: true,
    otroMetodo: true,
    comisionTarjeta: 2.9,
    comisionTransferencia: 0,
    datosFacturacion: false,

    // Pagos online (checkout)
    onlineActivo: false,
    onlineProveedor: 'transbank_webpay_plus',
    onlineModo: 'sandbox' as 'sandbox' | 'production',
    
    // Seguridad
    require2FA: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    
    // Integraciones
    qrAccess: false,
    apiKey: '',
    backupAutomatico: true,
    backupFrecuencia: 'diario'
  });

  const [savingConfig, setSavingConfig] = useState(false);
  const [pagoProviderSecrets, setPagoProviderSecrets] = useState<PagoProviderSecretsState>({
    commerceCode: '',
    apiKeySecret: '',
    configured: false,
    maskedCommerceCode: '',
    maskedApiKeySecret: '',
    loading: false,
    error: null,
  });

  const handleResetBrandingDefaults = () => {
    setConfigData((prev) => ({
      ...prev,
      colorPrimario: MOWE_DEFAULT_PRIMARY,
      colorSecundario: MOWE_DEFAULT_SECONDARY,
      temaOscuro: false,
    }));
  };

  const getConfigStorageKey = () => {
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId || 'sin-gimnasio';
    const sucursalId = sucursalActiva?.id || 'sin-sucursal';
    return `gymadmin_config_${gimnasioId}_${sucursalId}`;
  };

  const getCurrentGymId = () => (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId || null;

  const formatTiempoRelativo = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';

    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    const diffMin = Math.floor(diffSec / 60);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffSec < 60) return 'Hace 1 min';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffH < 24) return `Hace ${diffH} h`;
    if (diffD === 1) return 'Ayer';
    return `Hace ${diffD} días`;
  };

  const loadNotificaciones = async () => {
    const gimnasioId = getCurrentGymId();
    if (!profile) return;
    if (!gimnasioId && profile.role !== 'platform_admin') return;

    try {
      setNotificacionesLoading(true);
      setNotificacionesError(null);

      let q = supabase
        .from('notificaciones')
        .select('id,gimnasio_id,usuario_id,destino,tipo,titulo,mensaje,meta,leida,created_at')
        .eq('destino', 'admin')
        .order('created_at', { ascending: false })
        .limit(20);

      if (gimnasioId) q = q.eq('gimnasio_id', gimnasioId);

      const { data, error } = await q;
      if (error) throw error;

      const rows = (data || []) as any as NotificacionRow[];
      setNotificaciones(
        rows.map((r) => ({
          id: r.id,
          tipo: r.tipo,
          titulo: r.titulo,
          mensaje: r.mensaje,
          leida: !!r.leida,
          created_at: r.created_at,
          tiempo: formatTiempoRelativo(r.created_at),
        }))
      );
    } catch (err: any) {
      console.error('Error cargando notificaciones:', err);
      setNotificacionesError(err?.message || 'No se pudieron cargar las notificaciones.');
      setNotificaciones([]);
    } finally {
      setNotificacionesLoading(false);
    }
  };

  const marcarNotificacionLeida = async (id: string) => {
    try {
      const { error } = await supabase.from('notificaciones').update({ leida: true }).eq('id', id);
      if (error) throw error;
      setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    } catch (err) {
      console.error('Error marcando notificación como leída:', err);
    }
  };

  // Load in background so the bell can show unread dot without needing to open the dropdown.
  useEffect(() => {
    void loadNotificaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.gimnasioId, (sucursalActiva as any)?.gimnasio_id]);

  // Realtime: push new admin notifications without requiring a refresh.
  useEffect(() => {
    if (!profile) return;

    const gimnasioId = getCurrentGymId();
    if (!gimnasioId && profile.role !== 'platform_admin') return;

    const baseInsert = {
      event: 'INSERT' as const,
      schema: 'public',
      table: 'notificaciones',
    };
    const baseUpdate = {
      event: 'UPDATE' as const,
      schema: 'public',
      table: 'notificaciones',
    };

    const insertFilter = gimnasioId ? { ...baseInsert, filter: `gimnasio_id=eq.${gimnasioId}` } : baseInsert;
    const updateFilter = gimnasioId ? { ...baseUpdate, filter: `gimnasio_id=eq.${gimnasioId}` } : baseUpdate;

    const channel = supabase
      .channel(`notificaciones_admin_${gimnasioId || 'all'}`)
      .on('postgres_changes', insertFilter as any, (payload: any) => {
        const n = payload?.new as NotificacionRow | undefined;
        if (!n) return;
        if (n.destino !== 'admin') return;
        if (gimnasioId && n.gimnasio_id !== gimnasioId) return;

        setNotificaciones((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev;
          const next: NotificacionUI = {
            id: n.id,
            tipo: n.tipo,
            titulo: n.titulo,
            mensaje: n.mensaje,
            leida: !!n.leida,
            created_at: n.created_at,
            tiempo: formatTiempoRelativo(n.created_at),
          };
          return [next, ...prev].slice(0, 20);
        });
      })
      .on('postgres_changes', updateFilter as any, (payload: any) => {
        const n = payload?.new as Partial<NotificacionRow> | undefined;
        if (!n?.id) return;
        if (n.destino && n.destino !== 'admin') return;
        if (gimnasioId && n.gimnasio_id && n.gimnasio_id !== gimnasioId) return;

        if (typeof n.leida === 'boolean') {
          setNotificaciones((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: n.leida! } : x)));
        }
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, profile?.gimnasioId, (sucursalActiva as any)?.gimnasio_id]);

  type UsuarioAdminRow = {
    id: string;
    auth_id: string;
    email: string;
    nombre: string;
    role: 'platform_admin' | 'gym_admin' | 'staff' | 'admin';
    gimnasio_id: string | null;
    sucursal_id: string | null;
    activo: boolean;
    created_at: string;
  };

  const [usuariosAdmin, setUsuariosAdmin] = useState<UsuarioAdminRow[]>([]);
  const [usuariosAdminLoading, setUsuariosAdminLoading] = useState(false);
  const [usuariosAdminError, setUsuariosAdminError] = useState<string | null>(null);
  const [inviteAdminOpen, setInviteAdminOpen] = useState(false);
  const [inviteAdminForm, setInviteAdminForm] = useState({
    email: '',
    nombre: '',
    role: 'staff' as 'gym_admin' | 'staff' | 'admin',
    sucursal_id: '' as string,
  });

  const loadUsuariosAdmin = async () => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      setUsuariosAdminError('No se pudo resolver el gimnasio.');
      setUsuariosAdmin([]);
      return;
    }

    try {
      setUsuariosAdminLoading(true);
      setUsuariosAdminError(null);

      const { data, error } = await supabase
        .from('usuarios_admin')
        .select('id,auth_id,email,nombre,role,gimnasio_id,sucursal_id,activo,created_at')
        .eq('gimnasio_id', gimnasioId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsuariosAdmin((data || []) as any);
    } catch (err: any) {
      setUsuariosAdminError(err?.message || 'No se pudieron cargar los usuarios admin.');
      setUsuariosAdmin([]);
    } finally {
      setUsuariosAdminLoading(false);
    }
  };

  const handleInviteAdmin = async () => {
    const gimnasioId = getCurrentGymId();
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio.');
      return;
    }

    const email = inviteAdminForm.email.trim().toLowerCase();
    const nombre = inviteAdminForm.nombre.trim();
    if (!email || !email.includes('@')) {
      alert('Ingresa un email válido.');
      return;
    }

    const sucursalId = inviteAdminForm.sucursal_id.trim();

    const { data, error } = await supabase.functions.invoke('invitar-admin-gimnasio', {
      body: {
        gimnasio_id: gimnasioId,
        email,
        nombre: nombre || null,
        role: inviteAdminForm.role,
        sucursal_id: sucursalId ? sucursalId : null,
      },
    });

    if (error) {
      console.error('Error invitar-admin-gimnasio:', error);
      alert('No se pudo invitar al usuario.');
      return;
    }

    if ((data as any)?.error) {
      alert(String((data as any).error));
      return;
    }

    setInviteAdminOpen(false);
    setInviteAdminForm({ email: '', nombre: '', role: 'staff', sucursal_id: '' });
    await loadUsuariosAdmin();

    const accessLink = (data as any)?.access_link as string | null;
    if (accessLink) {
      try {
        await navigator.clipboard.writeText(accessLink);
        alert('Invitación creada. Copié el link de acceso en tu portapapeles.');
      } catch (_e) {
        alert('Invitación creada.');
      }
    } else {
      alert('Invitación creada.');
    }
  };

  const handleUpdateUsuarioAdmin = async (authId: string, patch: { activo?: boolean; role?: string; sucursal_id?: string | null }) => {
    const { data, error } = await supabase.functions.invoke('usuarios-admin-update', {
      body: {
        auth_id: authId,
        ...patch,
      },
    });

    if (error) {
      console.error('Error usuarios-admin-update:', error);
      alert('No se pudo actualizar el usuario admin.');
      return;
    }

    if ((data as any)?.error) {
      alert(String((data as any).error));
      return;
    }

    await loadUsuariosAdmin();
  };

  // Estados para el perfil del usuario
  const [profileData, setProfileData] = useState({
    nombre: profile?.name || 'Administrador',
    email: profile?.email || 'admin@tugym.com',
    telefono: '+56 9 8765 4321',
    cargo:
      profile?.role === 'platform_admin' || profile?.role === 'admin' || profile?.role === 'gym_admin'
        ? 'Administrador'
        : 'Staff',
    fechaIngreso: '2023-01-15',
    avatar: '',
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const [profileTab, setProfileTab] = useState<'info' | 'password'>('info');

  useEffect(() => {
    const key = getConfigStorageKey();
    let scopedConfig: Partial<typeof configData> = {};
    try {
      const raw = localStorage.getItem(key);
      if (raw) scopedConfig = JSON.parse(raw);
    } catch (_e) {
      scopedConfig = {};
    }

    const gimnasioId = getCurrentGymId();
    let cancelled = false;

    const run = async () => {
      // Best-effort: apply cached theme immediately to avoid flashing defaults while we fetch DB branding.
      try {
        if (gimnasioId) {
          const cachedGym = localStorage.getItem(`gymadmin_branding_${gimnasioId}`);
          if (cachedGym) applyBrandingTheme(JSON.parse(cachedGym));
        }

        const cachedScoped: any = scopedConfig || {};
        if (cachedScoped.colorPrimario || cachedScoped.colorSecundario || typeof cachedScoped.temaOscuro === 'boolean') {
          applyBrandingTheme({
            colorPrimario: cachedScoped.colorPrimario,
            colorSecundario: cachedScoped.colorSecundario,
            temaOscuro: cachedScoped.temaOscuro,
          });
        }
      } catch (_e) {
        // ignore
      }

      let gymBranding: { color_primario?: string | null; color_secundario?: string | null; tema_oscuro?: boolean | null } | null =
        null;

      if (gimnasioId) {
        const { data, error } = await supabase
          .from('gimnasios')
          .select('color_primario, color_secundario, tema_oscuro')
          .eq('id', gimnasioId)
          .maybeSingle();

        if (!error) {
          gymBranding = data as any;
        }
      }

      if (cancelled) return;

      setConfigData((prev) => ({
        ...prev,
        nombreGimnasio: sucursalActiva?.nombre || prev.nombreGimnasio,
        direccion: sucursalActiva?.direccion || prev.direccion,
        telefono: sucursalActiva?.telefono || prev.telefono,
        email: sucursalActiva?.email || prev.email,
        ...scopedConfig,
        colorPrimario: gymBranding?.color_primario ?? (scopedConfig as any)?.colorPrimario ?? prev.colorPrimario,
        colorSecundario: gymBranding?.color_secundario ?? (scopedConfig as any)?.colorSecundario ?? prev.colorSecundario,
        temaOscuro: gymBranding?.tema_oscuro ?? (scopedConfig as any)?.temaOscuro ?? prev.temaOscuro,
        idioma: 'es',
        datosFacturacion: false,
      }));
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursalActiva?.id, (sucursalActiva as any)?.gimnasio_id, profile?.gimnasioId]);

  useLayoutEffect(() => {
    applyBrandingTheme({
      colorPrimario: configData.colorPrimario,
      colorSecundario: configData.colorSecundario,
      temaOscuro: configData.temaOscuro,
    });
  }, [configData.colorPrimario, configData.colorSecundario, configData.temaOscuro]);

  useEffect(() => {
    // Persist last applied theme so we can apply it immediately on refresh (main.tsx) and on gym switch.
    try {
      const payload = {
        colorPrimario: configData.colorPrimario,
        colorSecundario: configData.colorSecundario,
        temaOscuro: configData.temaOscuro,
      };

      localStorage.setItem('gymadmin_branding_last', JSON.stringify(payload));

      const gimnasioId = getCurrentGymId();
      if (gimnasioId) {
        localStorage.setItem(`gymadmin_branding_${gimnasioId}`, JSON.stringify(payload));
      }
    } catch (_e) {
      // Best-effort only.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData.colorPrimario, configData.colorSecundario, configData.temaOscuro, sucursalActiva?.id]);

  const handleSaveProfile = () => {
    // Aquí se guardarían los datos del perfil
    setShowProfileModal(false);
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError(null);
      setPasswordSuccess(null);

      const newPassword = passwordData.newPassword.trim();
      const confirmPassword = passwordData.confirmPassword.trim();

      if (newPassword.length < 8) {
        setPasswordError('La nueva contraseña debe tener al menos 8 caracteres.');
        return;
      }

      if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
        setPasswordError('La nueva contraseña debe incluir mayúscula, minúscula y número.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError('Las contraseñas no coinciden.');
        return;
      }

      setPasswordSaving(true);

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw new Error(updateError.message || 'No se pudo actualizar la contraseña.');
      }

      setPasswordSuccess('Contraseña actualizada correctamente.');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordError(err?.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file, 'logo');
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file, 'avatar');
    }
  };

  const processImageFile = (file: File, type: 'logo' | 'avatar') => {
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 2MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      if (type === 'logo') {
        setConfigData(prev => ({ ...prev, logoUrl: event.target?.result as string }));
      } else {
        setProfileData(prev => ({ ...prev, avatar: event.target?.result as string }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Drag & Drop handlers para Logo
  const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processImageFile(files[0], 'logo');
    }
  };

  // Drag & Drop handlers para Avatar
  const handleAvatarDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(true);
  };

  const handleAvatarDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
  };

  const handleAvatarDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processImageFile(files[0], 'avatar');
    }
  };

  const menuItems = [
    { path: '/', icon: 'ri-dashboard-line', label: 'Dashboard' },
    ...(profile?.role === 'platform_admin'
      ? [
          { path: '/gimnasios', icon: 'ri-building-2-line', label: 'Gimnasios' },
          { path: '/saas-planes', icon: 'ri-price-tag-3-line', label: 'Planes SaaS' },
        ]
      : []),
    ...(profile?.role !== 'platform_admin'
      ? [{ path: '/pagar-servicio', icon: 'ri-wallet-3-line', label: 'Pagar Servicio' }]
      : []),
    { path: '/usuarios', icon: 'ri-user-line', label: 'Usuarios' },
    { path: '/planes', icon: 'ri-vip-crown-line', label: 'Planes' },
    { path: '/horarios', icon: 'ri-calendar-line', label: 'Horarios y Cupos' },
    { path: '/reservas', icon: 'ri-bookmark-line', label: 'Reservas' },
    { path: '/pagos', icon: 'ri-money-dollar-circle-line', label: 'Pagos' },
    { path: '/rutinas', icon: 'ri-heart-pulse-line', label: 'Rutinas' },
    { path: '/tutoriales', icon: 'ri-video-line', label: 'Tutoriales' },
    { path: '/reportes', icon: 'ri-bar-chart-box-line', label: 'Reportes' },
    { path: '/progreso', icon: 'ri-line-chart-line', label: 'Progreso' },
  ];

  const getNotificationIcon = (tipo: string) => {
    switch (tipo) {
      case 'pago': return 'ri-money-dollar-circle-fill text-green-500';
      case 'reserva': return 'ri-calendar-check-fill text-blue-500';
      case 'alerta': return 'ri-alarm-warning-fill text-orange-500';
      case 'usuario': return 'ri-user-add-fill text-teal-500';
      case 'vencimiento': return 'ri-time-fill text-red-500';
      default: return 'ri-notification-fill text-gray-500';
    }
  };

  const handleConfigItemClick = (itemId: string) => {
    setActiveConfigModal(itemId);
    setShowConfig(false);
  };

  const isPagoConfigModal = (id: string | null) =>
    id === 'metodos-pago' || id === 'comisiones';

  const loadPagoProviderSecrets = async (
    provider: string,
    mode: 'sandbox' | 'production',
    gimnasioIdParam?: string | null
  ) => {
    const gimnasioId = gimnasioIdParam || getCurrentGymId();
    if (!gimnasioId) return;

    if (provider !== 'transbank_webpay_plus') {
      setPagoProviderSecrets({
        commerceCode: '',
        apiKeySecret: '',
        configured: false,
        maskedCommerceCode: '',
        maskedApiKeySecret: '',
        loading: false,
        error: null,
      });
      return;
    }

    setPagoProviderSecrets((prev) => ({ ...prev, loading: true, error: null }));
    const { data, error } = await supabase.functions.invoke('payments-provider-secrets', {
      body: {
        action: 'get',
        gimnasio_id: gimnasioId,
        proveedor: provider,
        modo: mode,
      },
    });

    if (error) {
      console.error('Error cargando credenciales de pago:', error);
      setPagoProviderSecrets((prev) => ({
        ...prev,
        loading: false,
        configured: false,
        maskedCommerceCode: '',
        maskedApiKeySecret: '',
        error: 'No se pudieron cargar las credenciales del proveedor.',
      }));
      return;
    }

    if (data?.error) {
      setPagoProviderSecrets((prev) => ({
        ...prev,
        loading: false,
        configured: false,
        maskedCommerceCode: '',
        maskedApiKeySecret: '',
        error: String(data.error),
      }));
      return;
    }

    setPagoProviderSecrets((prev) => ({
      ...prev,
      loading: false,
      configured: !!data?.configured,
      maskedCommerceCode: String(data?.masked_commerce_code || ''),
      maskedApiKeySecret: String(data?.masked_api_key_secret || ''),
      error: null,
    }));
  };

  const loadPagoConfig = async () => {
    if (!sucursalActiva?.id) return;
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
    if (!gimnasioId) return;

    // Prefer sucursal-specific config, fallback to gym default.
    const { data: cfgSucursal } = await supabase
      .from('pago_configuracion')
      .select('*')
      .eq('gimnasio_id', gimnasioId)
      .eq('sucursal_id', sucursalActiva.id)
      .maybeSingle();

    const { data: cfgGym } = cfgSucursal
      ? { data: null as any }
      : await supabase
          .from('pago_configuracion')
          .select('*')
          .eq('gimnasio_id', gimnasioId)
          .is('sucursal_id', null)
          .maybeSingle();

    const cfg = cfgSucursal || cfgGym;
    const selectedProvider = String(cfg?.online_proveedor || configData.onlineProveedor || 'transbank_webpay_plus');
    const selectedMode = (cfg?.online_modo === 'production' ? 'production' : configData.onlineModo === 'production' ? 'production' : 'sandbox') as
      | 'sandbox'
      | 'production';

    if (cfg) {
      setConfigData((prev) => ({
        ...prev,
        efectivo: cfg.efectivo ?? prev.efectivo,
        tarjetaCredito: cfg.tarjeta_credito ?? prev.tarjetaCredito,
        tarjetaDebito: cfg.tarjeta_debito ?? prev.tarjetaDebito,
        transferencia: cfg.transferencia ?? prev.transferencia,
        otroMetodo: cfg.otro ?? prev.otroMetodo,
        comisionTarjeta: cfg.comision_tarjeta ?? prev.comisionTarjeta,
        comisionTransferencia: cfg.comision_transferencia ?? prev.comisionTransferencia,
        datosFacturacion: false,
        onlineActivo: cfg.online_activo ?? prev.onlineActivo,
        onlineProveedor: cfg.online_proveedor ?? prev.onlineProveedor,
        onlineModo: (cfg.online_modo === 'production' ? 'production' : 'sandbox') as 'sandbox' | 'production',
      }));
    }

    await loadPagoProviderSecrets(selectedProvider, selectedMode, gimnasioId);
  };

  useEffect(() => {
    if (!isPagoConfigModal(activeConfigModal)) return;
    loadPagoConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfigModal, sucursalActiva?.id]);

  useEffect(() => {
    if (activeConfigModal !== 'usuarios-admin') return;
    loadUsuariosAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfigModal, (sucursalActiva as any)?.gimnasio_id, profile?.gimnasioId]);

  const handleSaveConfig = async () => {
    if (!activeConfigModal) return;

    try {
      setSavingConfig(true);

      if (activeConfigModal === 'personalizacion') {
        const gimnasioId = getCurrentGymId();
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la personalización.');
          return;
        }

        const { error } = await supabase.rpc('set_gym_personalizacion', {
          p_gimnasio_id: gimnasioId,
          p_color_primario: configData.colorPrimario,
          p_color_secundario: configData.colorSecundario,
          p_tema_oscuro: !!configData.temaOscuro,
        });

        if (error) {
          console.error('Error guardando personalización del gimnasio:', error);
          alert('No se pudo guardar la personalización.');
          return;
        }
      }

      if (isPagoConfigModal(activeConfigModal)) {
        if (!sucursalActiva?.id) {
          alert('Debes seleccionar una sucursal antes de guardar la configuración de pagos.');
          return;
        }

        const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
        if (!gimnasioId) {
          alert('No se pudo resolver el gimnasio para guardar la configuración.');
          return;
        }

        const selectedProvider = String(configData.onlineProveedor || 'transbank_webpay_plus');
        const selectedMode = configData.onlineModo === 'production' ? 'production' : 'sandbox';

        const payload = {
          gimnasio_id: gimnasioId,
          sucursal_id: sucursalActiva.id,
          efectivo: !!configData.efectivo,
          tarjeta_credito: !!configData.tarjetaCredito,
          tarjeta_debito: !!configData.tarjetaDebito,
          transferencia: !!configData.transferencia,
          otro: !!configData.otroMetodo,
          comision_tarjeta: Number(configData.comisionTarjeta) || 0,
          comision_transferencia: Number(configData.comisionTransferencia) || 0,
          solicitar_facturacion: false,
          online_activo: !!configData.onlineActivo,
          online_proveedor: configData.onlineActivo ? selectedProvider : null,
          online_modo: selectedMode,
        };

        const { error } = await supabase.from('pago_configuracion').upsert(payload, {
          onConflict: 'gimnasio_id,sucursal_id',
        });

        if (error) {
          console.error('Error guardando pago_configuracion:', error);
          alert('No se pudo guardar la configuración de pagos.');
          return;
        }

        if (configData.onlineActivo && selectedProvider === 'transbank_webpay_plus') {
          const commerceCode = pagoProviderSecrets.commerceCode.trim();
          const apiKeySecret = pagoProviderSecrets.apiKeySecret.trim();
          const hasNewCredentials = commerceCode.length > 0 || apiKeySecret.length > 0;

          if (!pagoProviderSecrets.configured && !hasNewCredentials) {
            alert('Para habilitar pagos online debes ingresar código de comercio y API Key Secret de Transbank.');
            return;
          }

          if (hasNewCredentials) {
            const { data: secretData, error: secretError } = await supabase.functions.invoke('payments-provider-secrets', {
              body: {
                action: 'upsert',
                gimnasio_id: gimnasioId,
                proveedor: selectedProvider,
                modo: selectedMode,
                commerce_code: commerceCode || undefined,
                api_key_secret: apiKeySecret || undefined,
              },
            });

            if (secretError) {
              console.error('Error guardando credenciales de pago:', secretError);
              alert('No se pudieron guardar las credenciales del proveedor.');
              return;
            }

            if (secretData?.error) {
              alert(String(secretData.error));
              return;
            }

            setPagoProviderSecrets((prev) => ({
              ...prev,
              commerceCode: '',
              apiKeySecret: '',
              configured: !!secretData?.configured,
              maskedCommerceCode: String(secretData?.masked_commerce_code || ''),
              maskedApiKeySecret: String(secretData?.masked_api_key_secret || ''),
              error: null,
            }));
          } else {
            await loadPagoProviderSecrets(selectedProvider, selectedMode, gimnasioId);
          }
        }
      }

      if (activeConfigModal === 'datos-gimnasio' && sucursalActiva?.id) {
        const { error } = await supabase
          .from('sucursales')
          .update({
            nombre: configData.nombreGimnasio.trim() || sucursalActiva.nombre,
            direccion: configData.direccion.trim() || null,
            telefono: configData.telefono.trim() || null,
            email: configData.email.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', sucursalActiva.id);

        if (error) {
          console.error('Error guardando datos de sucursal:', error);
          alert('No se pudieron guardar los datos del gimnasio.');
          return;
        }

        await refreshSucursales();
      }

      try {
        localStorage.setItem(
          getConfigStorageKey(),
          JSON.stringify({
            ...configData,
            idioma: 'es',
            datosFacturacion: false,
          })
        );
      } catch (_e) {
        // Best-effort local persistence for non-DB settings.
      }

      setActiveConfigModal(null);
    } finally {
      setSavingConfig(false);
    }
  };

  const renderConfigModal = () => {
    if (!activeConfigModal) return null;

    if (activeConfigModal === 'sms-whatsapp' && !ENABLE_SMS_WHATSAPP_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'registro-actividad' && !ENABLE_REGISTRO_ACTIVIDAD_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'apis-externas' && !ENABLE_APIS_EXTERNAS_SETTINGS) {
      return null;
    }
    if (activeConfigModal === 'respaldos' && !ENABLE_RESPALDOS_SETTINGS) {
      return null;
    }

    const getModalContent = () => {
      switch (activeConfigModal) {
        case 'datos-gimnasio':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Datos del Gimnasio</h3>
                <p className="text-sm text-gray-500 mt-1">Información básica de tu establecimiento</p>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Gimnasio</label>
                    <input
                      type="text"
                      value={configData.nombreGimnasio}
                      onChange={(e) => setConfigData(prev => ({ ...prev, nombreGimnasio: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={configData.direccion}
                      onChange={(e) => setConfigData(prev => ({ ...prev, direccion: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={configData.telefono}
                        onChange={(e) => setConfigData(prev => ({ ...prev, telefono: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={configData.email}
                        onChange={(e) => setConfigData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sitio Web</label>
                    <input
                      type="url"
                      value={configData.sitioWeb}
                      onChange={(e) => setConfigData(prev => ({ ...prev, sitioWeb: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="hidden"
                    />
                    {configData.logoUrl ? (
                      <div 
                        className={`border-2 rounded-lg p-4 transition-all ${
                          isDraggingLogo 
                            ? 'border-teal-500 bg-teal-50 border-solid' 
                            : 'border-gray-200'
                        }`}
                        onDragOver={handleLogoDragOver}
                        onDragLeave={handleLogoDragLeave}
                        onDrop={handleLogoDrop}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                            <img 
                              src={configData.logoUrl} 
                              alt="Logo del gimnasio" 
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Logo cargado</p>
                            <p className="text-xs text-gray-500 mt-1">Arrastra una nueva imagen o usa los botones</p>
                            <div className="flex gap-2 mt-3">
                              <button 
                                onClick={() => logoInputRef.current?.click()}
                                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs cursor-pointer whitespace-nowrap hover:bg-teal-700 transition-colors"
                              >
                                <i className="ri-upload-2-line mr-1"></i>
                                Cambiar
                              </button>
                              <button 
                                onClick={() => setConfigData(prev => ({ ...prev, logoUrl: '' }))}
                                className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs cursor-pointer whitespace-nowrap hover:bg-red-200 transition-colors"
                              >
                                <i className="ri-delete-bin-line mr-1"></i>
                                Eliminar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
                          isDraggingLogo 
                            ? 'border-teal-500 bg-teal-50 scale-[1.02]' 
                            : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
                        }`}
                        onClick={() => logoInputRef.current?.click()}
                        onDragOver={handleLogoDragOver}
                        onDragLeave={handleLogoDragLeave}
                        onDrop={handleLogoDrop}
                      >
                        <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                          isDraggingLogo ? 'ring-4 ring-teal-300 scale-105' : 'bg-teal-100'
                        }`}>
                          <i className={`text-3xl transition-colors ${
                            isDraggingLogo ? 'ri-download-2-line text-teal-600' : 'ri-image-add-line text-gray-400'
                          }`}></i>
                        </div>
                        <p className={`text-sm font-medium transition-colors ${
                          isDraggingLogo ? 'text-teal-700' : 'text-gray-600'
                        }`}>
                          {isDraggingLogo ? '¡Suelta la imagen aquí!' : 'Arrastra y suelta tu logo aquí'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">o haz clic para seleccionar</p>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            logoInputRef.current?.click();
                          }}
                          className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-teal-700 transition-colors"
                        >
                          <i className="ri-folder-open-line mr-2"></i>
                          Seleccionar Archivo
                        </button>
                        <p className="text-xs text-gray-400 mt-3">PNG, JPG, GIF o WebP. Máximo 2MB</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          );

        case 'personalizacion':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Personalización</h3>
                    <p className="text-sm text-gray-500 mt-1">Colores, tema y apariencia de la app</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color Primario</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorPrimario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorPrimario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorPrimario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Color Secundario</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={configData.colorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSecundario: e.target.value }))}
                          className="w-12 h-10 p-0 border border-gray-300 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={configData.colorSecundario}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, colorSecundario: e.target.value }))}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Tema Oscuro</p>
                      <p className="text-xs text-gray-500 mt-1">Habilitar modo oscuro en la interfaz</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={configData.temaOscuro}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, temaOscuro: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </label>

                  <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Vista previa</p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="rounded-lg p-4 text-white" style={{ backgroundColor: configData.colorPrimario }}>
                        <p className="text-sm font-semibold">Primario</p>
                        <p className="text-xs opacity-90">{configData.colorPrimario}</p>
                      </div>
                      <div className="rounded-lg p-4 text-white" style={{ backgroundColor: configData.colorSecundario }}>
                        <p className="text-sm font-semibold">Secundario</p>
                        <p className="text-xs opacity-90">{configData.colorSecundario}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );

        case 'idioma-region':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Idioma y Región</h3>
                    <p className="text-sm text-gray-500 mt-1">Zona horaria, formato de fecha y moneda</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Idioma</label>
                    <select
                      value="es"
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 cursor-not-allowed"
                    >
                      <option value="es">Español</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">El idioma del portal está fijado en Español.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zona horaria</label>
                    <input
                      type="text"
                      value={configData.zonaHoraria}
                      onChange={(e) => setConfigData((prev) => ({ ...prev, zonaHoraria: e.target.value }))}
                      placeholder="America/Santiago"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Ejemplo: `America/Santiago`, `America/Bogota`, `America/Mexico_City`.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Moneda</label>
                      <select
                        value={configData.moneda}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, moneda: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        <option value="CLP">CLP</option>
                        <option value="ARS">ARS</option>
                        <option value="PEN">PEN</option>
                        <option value="COP">COP</option>
                        <option value="MXN">MXN</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Formato de fecha</label>
                      <select
                        value={configData.formatoFecha}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, formatoFecha: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        <option value="dd/MM/yyyy">dd/MM/yyyy</option>
                        <option value="MM/dd/yyyy">MM/dd/yyyy</option>
                        <option value="yyyy-MM-dd">yyyy-MM-dd</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );

        case 'alertas-push':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Alertas Push</h3>
                    <p className="text-sm text-gray-500 mt-1">Configurar notificaciones a usuarios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <ToggleRow
                    title="Reservas"
                    desc="Avisos al reservar, cancelar o cambios de cupos"
                    checked={configData.pushReservas}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushReservas: v }))}
                  />
                  <ToggleRow
                    title="Pagos"
                    desc="Confirmaciones y recordatorios de pago"
                    checked={configData.pushPagos}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushPagos: v }))}
                  />
                  <ToggleRow
                    title="Cancelaciones"
                    desc="Notificar cancelaciones de reservas"
                    checked={configData.pushCancelaciones}
                    onChange={(v) => setConfigData((p) => ({ ...p, pushCancelaciones: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'correos-automaticos':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Correos Automáticos</h3>
                    <p className="text-sm text-gray-500 mt-1">Plantillas de email y recordatorios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <ToggleRow
                    title="Email de bienvenida"
                    desc="Enviar al crear usuario y activar cuenta"
                    checked={configData.emailBienvenida}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailBienvenida: v }))}
                  />
                  <ToggleRow
                    title="Recordatorios"
                    desc="Recordatorios de vencimiento / asistencia"
                    checked={configData.emailRecordatorios}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailRecordatorios: v }))}
                  />
                  <ToggleRow
                    title="Vencimientos"
                    desc="Avisos de membresías por vencer"
                    checked={configData.emailVencimientos}
                    onChange={(v) => setConfigData((p) => ({ ...p, emailVencimientos: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'sms-whatsapp':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">SMS y WhatsApp</h3>
                    <p className="text-sm text-gray-500 mt-1">Mensajes de confirmación y avisos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-3">
                  <ToggleRow
                    title="SMS"
                    desc="Enviar SMS en confirmaciones"
                    checked={configData.smsConfirmaciones}
                    onChange={(v) => setConfigData((p) => ({ ...p, smsConfirmaciones: v }))}
                  />
                  <ToggleRow
                    title="WhatsApp"
                    desc="Enviar mensajes por WhatsApp"
                    checked={configData.whatsappAvisos}
                    onChange={(v) => setConfigData((p) => ({ ...p, whatsappAvisos: v }))}
                  />
                </div>
              </div>
            </>
          );

        case 'comisiones':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Comisiones</h3>
                    <p className="text-sm text-gray-500 mt-1">Configurar tasas y recargos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Comisión tarjeta</p>
                    <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a pagos con tarjeta</p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={configData.comisionTarjeta}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTarjeta: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Comisión transferencia</p>
                    <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a transferencias</p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={configData.comisionTransferencia}
                        onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTransferencia: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );

        case 'usuarios-admin':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Usuarios Admin</h3>
                    <p className="text-sm text-gray-500 mt-1">Gestionar accesos del personal</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-gray-600">
                        Estos usuarios controlan el portal del gimnasio. La visibilidad es por gimnasio (multi-tenant).
                      </p>
                    </div>
                    <button
                      onClick={() => setInviteAdminOpen((v) => !v)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Invitar
                    </button>
                  </div>

                  {inviteAdminOpen && (
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={inviteAdminForm.email}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder="staff@gimnasio.com"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                          <input
                            type="text"
                            value={inviteAdminForm.nombre}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, nombre: e.target.value }))}
                            placeholder="Nombre y apellido"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                          <select
                            value={inviteAdminForm.role}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, role: e.target.value as any }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                            <option value="gym_admin">Gym Admin</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-2">No existe el rol owner aquí (platform_admin).</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Sucursal (opcional)</label>
                          <select
                            value={inviteAdminForm.sucursal_id}
                            onChange={(e) => setInviteAdminForm((p) => ({ ...p, sucursal_id: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="">Todas</option>
                            {(sucursalesDB || []).map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.nombre}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => setInviteAdminOpen(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors cursor-pointer text-sm"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleInviteAdmin}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer text-sm"
                        >
                          Enviar invitación
                        </button>
                      </div>
                    </div>
                  )}

                  {usuariosAdminError && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                      {usuariosAdminError}
                    </div>
                  )}

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">Personal</p>
                      <button
                        onClick={loadUsuariosAdmin}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        Recargar
                      </button>
                    </div>

                    {usuariosAdminLoading ? (
                      <div className="p-4 text-sm text-gray-600">Cargando...</div>
                    ) : usuariosAdmin.length === 0 ? (
                      <div className="p-4 text-sm text-gray-600">Aún no hay usuarios admin para este gimnasio.</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {usuariosAdmin.map((ua) => (
                          <div key={ua.id} className="p-4 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 truncate">{ua.nombre}</p>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
                                  {ua.role}
                                </span>
                                {!ua.activo && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 whitespace-nowrap">
                                    inactivo
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 truncate">{ua.email}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Sucursal: {ua.sucursal_id ? (sucursalesDB || []).find((s) => s.id === ua.sucursal_id)?.nombre || 'Asignada' : 'Todas'}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <select
                                value={ua.role}
                                onChange={(e) => handleUpdateUsuarioAdmin(ua.auth_id, { role: e.target.value })}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                disabled={ua.role === 'platform_admin'}
                              >
                                <option value="staff">staff</option>
                                <option value="admin">admin</option>
                                <option value="gym_admin">gym_admin</option>
                              </select>

                              <button
                                onClick={() => handleUpdateUsuarioAdmin(ua.auth_id, { activo: !ua.activo })}
                                className={`px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                                  ua.activo ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-teal-600 hover:bg-teal-700 text-white'
                                }`}
                              >
                                {ua.activo ? 'Desactivar' : 'Activar'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          );

        case 'contrasenas':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Contraseñas</h3>
                    <p className="text-sm text-gray-500 mt-1">Políticas de seguridad</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitud mínima</label>
                    <input
                      type="number"
                      min={6}
                      value={configData.passwordMinLength}
                      onChange={(e) => setConfigData((p) => ({ ...p, passwordMinLength: Number(e.target.value) }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Esto es una preferencia del portal; en Supabase Auth se configura por separado.</p>
                  </div>
                </div>
              </div>
            </>
          );

        case 'registro-actividad':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Registro de Actividad</h3>
                    <p className="text-sm text-gray-500 mt-1">Historial de acciones del sistema</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">Pendiente</p>
                  <p className="text-sm text-gray-600 mt-1">
                    No existe aún una tabla de auditoría. Si la necesitas, la implementamos con triggers/RPC y se muestra aquí.
                  </p>
                </div>
              </div>
            </>
          );

        case 'control-acceso':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Control de Acceso</h3>
                    <p className="text-sm text-gray-500 mt-1">QR, torniquetes y lectores</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <ToggleRow
                    title="Acceso por QR"
                    desc="Permite validar entrada mediante QR (base para torniquete)"
                    checked={configData.qrAccess}
                    onChange={(v) => setConfigData((p) => ({ ...p, qrAccess: v }))}
                  />
                  <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Futuro</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Cuando integremos torniquete/lector, conectamos esta configuración con una API externa y reglas de acceso (plan, asistencias, etc.).
                    </p>
                  </div>
                </div>
              </div>
            </>
          );

        case 'apis-externas':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">APIs Externas</h3>
                    <p className="text-sm text-gray-500 mt-1">Conexiones con otros servicios</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                    <input
                      type="text"
                      value={configData.apiKey}
                      onChange={(e) => setConfigData((p) => ({ ...p, apiKey: e.target.value }))}
                      placeholder="(opcional)"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">Recomendación: mover esto a Secrets/Edge Functions si se usa en producción.</p>
                  </div>
                </div>
              </div>
            </>
          );

        case 'respaldos':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Respaldos</h3>
                    <p className="text-sm text-gray-500 mt-1">Backup automático de datos</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <ToggleRow
                    title="Respaldo automático"
                    desc="Habilitar backups programados"
                    checked={configData.backupAutomatico}
                    onChange={(v) => setConfigData((p) => ({ ...p, backupAutomatico: v }))}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frecuencia</label>
                    <select
                      value={configData.backupFrecuencia}
                      onChange={(e) => setConfigData((p) => ({ ...p, backupFrecuencia: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    >
                      <option value="diario">Diario</option>
                      <option value="semanal">Semanal</option>
                      <option value="mensual">Mensual</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          );

        case 'metodos-pago':
          return (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Métodos de Pago</h3>
                    <p className="text-sm text-gray-500 mt-1">Configura qué medios de pago aceptas y sus comisiones</p>
                  </div>
                  <button
                    onClick={() => setActiveConfigModal(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    aria-label="Cerrar"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-6">
                  <div className="p-4 bg-teal-50 border border-teal-100 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="text-sm font-medium text-teal-900">Nota</p>
                        <p className="text-sm text-teal-800 mt-1">
                          Esta configuración define medios aceptados y también permite guardar credenciales de checkout online (Transbank)
                          sin usar SQL manual.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Pagos online</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Habilita checkout para que el cliente pague desde la app (por ahora: Transbank).
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={configData.onlineActivo}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setConfigData((prev) => ({ ...prev, onlineActivo: enabled }));
                          if (enabled) {
                            loadPagoProviderSecrets(
                              configData.onlineProveedor || 'transbank_webpay_plus',
                              configData.onlineModo === 'production' ? 'production' : 'sandbox',
                              getCurrentGymId()
                            );
                          }
                        }}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer mt-1"
                      />
                    </div>

                    {configData.onlineActivo && (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Proveedor</label>
                          <select
                            value={configData.onlineProveedor}
                            onChange={(e) => {
                              const nextProvider = e.target.value;
                              setConfigData((prev) => ({ ...prev, onlineProveedor: nextProvider }));
                              setPagoProviderSecrets((prev) => ({
                                ...prev,
                                commerceCode: '',
                                apiKeySecret: '',
                              }));
                              loadPagoProviderSecrets(
                                nextProvider,
                                configData.onlineModo === 'production' ? 'production' : 'sandbox',
                                getCurrentGymId()
                              );
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="transbank_webpay_plus">Transbank Webpay Plus</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Modo</label>
                          <select
                            value={configData.onlineModo}
                            onChange={(e) => {
                              const nextMode = e.target.value === 'production' ? 'production' : 'sandbox';
                              setConfigData((prev) => ({
                                ...prev,
                                onlineModo: nextMode,
                              }));
                              setPagoProviderSecrets((prev) => ({
                                ...prev,
                                commerceCode: '',
                                apiKeySecret: '',
                              }));
                              loadPagoProviderSecrets(
                                configData.onlineProveedor || 'transbank_webpay_plus',
                                nextMode,
                                getCurrentGymId()
                              );
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          >
                            <option value="sandbox">Sandbox</option>
                            <option value="production">Producción</option>
                          </select>
                        </div>

                        {configData.onlineProveedor === 'transbank_webpay_plus' && (
                          <div className="sm:col-span-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                            <p className="text-xs font-semibold text-gray-900 mb-3">Credenciales Transbank</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Código de comercio</label>
                                <input
                                  type="text"
                                  value={pagoProviderSecrets.commerceCode}
                                  onChange={(e) =>
                                    setPagoProviderSecrets((prev) => ({
                                      ...prev,
                                      commerceCode: e.target.value,
                                      error: null,
                                    }))
                                  }
                                  placeholder="Ej: 597055555532"
                                  autoComplete="off"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">API Key Secret</label>
                                <input
                                  type="password"
                                  value={pagoProviderSecrets.apiKeySecret}
                                  onChange={(e) =>
                                    setPagoProviderSecrets((prev) => ({
                                      ...prev,
                                      apiKeySecret: e.target.value,
                                      error: null,
                                    }))
                                  }
                                  placeholder="Ingresa nueva API Key Secret"
                                  autoComplete="new-password"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                              </div>
                            </div>

                            <div className="mt-3 space-y-1">
                              {pagoProviderSecrets.loading ? (
                                <p className="text-xs text-gray-500">Cargando estado de credenciales...</p>
                              ) : pagoProviderSecrets.configured ? (
                                <p className="text-xs text-emerald-700">
                                  Configuradas: commerce `{pagoProviderSecrets.maskedCommerceCode || '***'}` y secret `{pagoProviderSecrets.maskedApiKeySecret || '***'}`
                                </p>
                              ) : (
                                <p className="text-xs text-amber-700">
                                  Aún no hay credenciales guardadas para este modo.
                                </p>
                              )}

                              {pagoProviderSecrets.error && (
                                <p className="text-xs text-red-600">{pagoProviderSecrets.error}</p>
                              )}

                              <p className="text-xs text-gray-500">
                                Si dejas un campo vacío, se mantiene su valor actual. El secreto nunca se muestra completo.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-3">Medios aceptados</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <i className="ri-money-dollar-circle-line text-green-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Efectivo</p>
                            <p className="text-xs text-gray-500">Pago presencial</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.efectivo}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, efectivo: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-bank-card-line text-blue-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Tarjeta crédito</p>
                            <p className="text-xs text-gray-500">Crédito</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.tarjetaCredito}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, tarjetaCredito: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <i className="ri-bank-card-2-line text-indigo-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Tarjeta débito</p>
                            <p className="text-xs text-gray-500">Débito</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.tarjetaDebito}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, tarjetaDebito: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <i className="ri-exchange-line text-amber-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Transferencia</p>
                            <p className="text-xs text-gray-500">Transferencia bancaria</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.transferencia}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, transferencia: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <i className="ri-more-2-line text-gray-700 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Otro</p>
                            <p className="text-xs text-gray-500">Cheque, depósito u otros</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={configData.otroMetodo}
                          onChange={(e) => setConfigData((prev) => ({ ...prev, otroMetodo: e.target.checked }))}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-3">Comisiones</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Comisión tarjeta</p>
                            <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a pagos con tarjeta</p>
                          </div>
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-percent-line text-blue-700 text-xl"></i>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={configData.comisionTarjeta}
                            onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTarjeta: Number(e.target.value) }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>

                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Comisión transferencia</p>
                            <p className="text-xs text-gray-500 mt-1">Porcentaje aplicado a transferencias</p>
                          </div>
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <i className="ri-percent-line text-amber-700 text-xl"></i>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={configData.comisionTransferencia}
                            onChange={(e) => setConfigData((prev) => ({ ...prev, comisionTransferencia: Number(e.target.value) }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => setActiveConfigModal(null)}
          ></div>

          <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
            {getModalContent()}
            
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              {activeConfigModal === 'personalizacion' && (
                <button
                  onClick={handleResetBrandingDefaults}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Por defecto
                </button>
              )}
              <button 
                onClick={() => setActiveConfigModal(null)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  savingConfig ? 'bg-teal-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                <i className={`${savingConfig ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} mr-2`}></i>
                {savingConfig ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ToggleRow = ({
    title,
    desc,
    checked,
    onChange,
  }: {
    title: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => {
    return (
      <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
        <div>
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="text-xs text-gray-500 mt-1">{desc}</p>
        </div>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
        />
      </label>
    );
  };

  const handleLogout = async () => {
    if (confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      await signOut();
      navigate('/login');
    }
  };

  const handleSucursalChange = (sucursal: any) => {
    cambiarSucursal(sucursal);
    setShowSucursalSelector(false);
  };

  const handleOpenNewSucursal = () => {
    setSelectedSucursal(null);
    setSucursalForm(buildSucursalForm());
    setShowNewSucursalModal(true);
    setShowSucursalesModal(false);
  };

  const handleEditSucursal = (sucursal: any) => {
    setSelectedSucursal(sucursal);
    setSucursalForm(buildSucursalForm(sucursal));
    setShowEditSucursalModal(true);
    setShowSucursalesModal(false);
  };

  const handleDeleteSucursal = async (sucursal: any) => {
    if (!sucursal?.id) return;

    if (sucursalesDB.length <= 1) {
      alert('No puedes eliminar la única sucursal del gimnasio.');
      return;
    }

    const ok = confirm(
      `¿Eliminar la sucursal "${sucursal.nombre}"?\n\n` +
        'Solo se puede eliminar si no tiene datos asociados (usuarios, horarios, reservas, pagos, etc.).'
    );
    if (!ok) return;

    try {
      setDeletingSucursalId(sucursal.id);

      const [
        usuariosRes,
        horariosRes,
        cuposRes,
        feriadosRes,
        pagosRes,
        reservasRes,
      ] = await Promise.all([
        supabase.from('usuarios').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('horarios').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('configuracion_cupos').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('feriados').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase.from('pagos').select('id', { head: true, count: 'exact' }).eq('sucursal_id', sucursal.id),
        supabase
          .from('reservas')
          .select('id, horarios!inner(sucursal_id)', { head: true, count: 'exact' })
          .eq('horarios.sucursal_id', sucursal.id),
      ]);

      const errors = [usuariosRes.error, horariosRes.error, cuposRes.error, feriadosRes.error, pagosRes.error, reservasRes.error].filter(Boolean);
      if (errors.length > 0) {
        console.error('Error validando eliminación de sucursal:', errors);
        alert('No se pudo validar la sucursal. Revisa permisos e intenta nuevamente.');
        return;
      }

      const bloqueos = [
        { label: 'usuarios', count: usuariosRes.count || 0 },
        { label: 'horarios', count: horariosRes.count || 0 },
        { label: 'configuración de cupos', count: cuposRes.count || 0 },
        { label: 'feriados', count: feriadosRes.count || 0 },
        { label: 'pagos', count: pagosRes.count || 0 },
        { label: 'reservas', count: reservasRes.count || 0 },
      ].filter((b) => b.count > 0);

      if (bloqueos.length > 0) {
        const detalle = bloqueos.map((b) => `- ${b.label}: ${b.count}`).join('\n');
        alert(
          'No puedes eliminar esta sucursal porque tiene datos asociados:\n\n' +
            `${detalle}\n\n` +
            'Primero limpia o migra esos datos.'
        );
        return;
      }

      const { error: deleteError } = await supabase.from('sucursales').delete().eq('id', sucursal.id);
      if (deleteError) {
        throw deleteError;
      }

      const nextSucursal =
        sucursalActiva?.id === sucursal.id
          ? sucursalesDB.find((s) => s.id !== sucursal.id) || null
          : null;

      await refreshSucursales();
      if (nextSucursal) {
        cambiarSucursal(nextSucursal as any);
      }
    } catch (error: any) {
      console.error('Error eliminando sucursal:', error);
      alert(error?.message || 'No se pudo eliminar la sucursal.');
    } finally {
      setDeletingSucursalId(null);
    }
  };

  const handleHorarioSucursalChange = (dia: DiaSemana, patch: Partial<HorarioDia>) => {
    setSucursalForm((prev) => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          ...prev.horarios[dia],
          ...patch,
        },
      },
    }));
  };

  const handleSaveSucursal = async () => {
    const gimnasioId = (sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId;
    if (!gimnasioId) {
      alert('No se pudo resolver el gimnasio para guardar la sucursal.');
      return;
    }

    const nombre = sucursalForm.nombre.trim();
    if (!nombre) {
      alert('El nombre de la sucursal es obligatorio.');
      return;
    }

    const payload = {
      gimnasio_id: gimnasioId,
      nombre,
      estado: sucursalForm.estado,
      direccion: sucursalForm.direccion.trim() || null,
      telefono: sucursalForm.telefono.trim() || null,
      email: sucursalForm.email.trim() || null,
      capacidad_maxima: Number(sucursalForm.capacidad_maxima) || null,
      horarios: sucursalForm.horarios,
      updated_at: new Date().toISOString(),
    };

    try {
      if (showNewSucursalModal) {
        const { data, error } = await supabase
          .from('sucursales')
          .insert([payload])
          .select('*')
          .single();

        if (error) throw error;

        await refreshSucursales();
        if (data) cambiarSucursal(data as any);
      } else if (selectedSucursal?.id) {
        const { error } = await supabase
          .from('sucursales')
          .update(payload)
          .eq('id', selectedSucursal.id);

        if (error) throw error;

        await refreshSucursales();
      }

      setShowEditSucursalModal(false);
      setShowNewSucursalModal(false);
      setSelectedSucursal(null);
      setSucursalForm(buildSucursalForm());
    } catch (error: any) {
      console.error('Error guardando sucursal:', error);
      alert(error?.message || 'No se pudo guardar la sucursal.');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen && (
            <h1 className="text-xl font-bold text-teal-600">MOWE</h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className={`${sidebarOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} text-xl text-gray-600`}></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3 mx-2 mb-1 rounded-lg transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className={`${item.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
                {sidebarOpen && <span className="ml-3 whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Profile in Sidebar */}
        <div className="p-4 border-t border-gray-200 relative">
          <button
            onClick={() => setShowSidebarUserMenu(!showSidebarUserMenu)}
            className="w-full flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium flex-shrink-0">
              {profileData.avatar ? (
                <img src={profileData.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
              ) : (
                profile?.name?.charAt(0) || 'A'
              )}
            </div>
            {sidebarOpen && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{profileData.nombre}</p>
                  <p className="text-xs text-gray-500 truncate">{profileData.email}</p>
                </div>
                <i className={`ri-more-2-fill text-gray-400 flex-shrink-0 transition-transform ${showSidebarUserMenu ? 'rotate-90' : ''}`}></i>
              </>
            )}
          </button>

          {/* Sidebar User Menu Popup */}
          {showSidebarUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowSidebarUserMenu(false)}
              ></div>
              <div className={`absolute ${sidebarOpen ? 'left-2 right-2' : 'left-full ml-2 w-56'} bottom-full mb-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50`}>
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{profileData.nombre}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{profileData.cargo}</p>
                </div>
                <div className="py-1">
                  <button 
                    onClick={() => {
                      setShowProfileModal(true);
                      setShowSidebarUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-user-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                    Mi Perfil
                  </button>
                  <button 
                    onClick={() => {
                      setShowConfig(true);
                      setShowSidebarUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <i className="ri-settings-3-line text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                    Configuración
                  </button>
                </div>
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={() => {
                      setShowSidebarUserMenu(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-logout-box-line w-5 h-5 flex items-center justify-center"></i>
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </>
          )}
        </div> {/* <-- Cierre del contenedor interno del sidebar */}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
              
              {/* Selector de Sucursal */}
              <div className="relative">
                <button
                  onClick={() => setShowSucursalSelector(!showSucursalSelector)}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer border border-teal-200"
                >
                  <i className="ri-building-line text-lg"></i>
                  <span className="text-sm font-medium">{sucursalActiva?.nombre || 'Seleccionar sucursal'}</span>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>

                {/* Dropdown de Sucursales */}
                {showSucursalSelector && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowSucursalSelector(false)}
                    ></div>
                    <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-20">
                      <div className="p-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-gray-900">Seleccionar Sucursal</h3>
                          <button
                            onClick={() => {
                              setShowSucursalesModal(true);
                              setShowSucursalSelector(false);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer"
                            title="Gestionar sucursales"
                          >
                            <i className="ri-settings-3-line text-gray-600"></i>
                          </button>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto p-2">
                        {sucursalesDB.map((sucursal) => (
                          <button
                            key={sucursal.id}
                            onClick={() => handleSucursalChange(sucursal)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                              sucursalActiva?.id === sucursal.id ? 'bg-teal-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                sucursalActiva?.id === sucursal.id ? 'bg-teal-600' : 'bg-gray-200'
                              }`}>
                                <i className={`ri-building-line text-lg ${
                                  sucursalActiva?.id === sucursal.id ? 'text-white' : 'text-gray-600'
                                }`}></i>
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-900">{sucursal.nombre}</p>
                                <p className="text-xs text-gray-500">
                                  {[
                                    sucursal.direccion || 'Sin dirección',
                                    sucursal.gym_nombre ? `Gym: ${sucursal.gym_nombre}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                              </div>
                            </div>
                            {sucursalActiva?.id === sucursal.id && (
                              <i className="ri-check-line text-teal-600 text-lg"></i>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setShowSucursalesModal(true);
                            setShowSucursalSelector(false);
                          }}
                          className="w-full py-2 text-sm text-teal-600 font-medium hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Gestionar Sucursales
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Notifications Button */}
              <div className="relative">
                <button 
                  onClick={() => {
                    const next = !showNotifications;
                    setShowNotifications(next);
                    setShowConfig(false);
                    if (next) void loadNotificaciones();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer relative"
                >
                  <i className="ri-notification-line text-xl text-gray-600"></i>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Notificaciones</h3>
                        {unreadCount > 0 && (
                          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                            {unreadCount} nuevas
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notificacionesLoading && (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <p className="mt-2 text-sm">Cargando notificaciones...</p>
                        </div>
                      )}

                      {!notificacionesLoading && notificacionesError && (
                        <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
                          {notificacionesError}
                        </div>
                      )}

                      {!notificacionesLoading && !notificacionesError && notificaciones.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-notification-off-line text-2xl"></i>
                          <p className="mt-2 text-sm">No hay notificaciones</p>
                        </div>
                      )}

                      {!notificacionesLoading && !notificacionesError && notificaciones.map((notif) => (
                        <button
                          type="button"
                          key={notif.id}
                          onClick={() => {
                            if (!notif.leida) void marcarNotificacionLeida(notif.id);
                          }}
                          className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                            !notif.leida ? 'bg-teal-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <i className={`${getNotificationIcon(notif.tipo)} text-lg`}></i>
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900">{notif.titulo}</p>
                                {!notif.leida && <span className="w-2 h-2 bg-teal-500 rounded-full"></span>}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">{notif.mensaje}</p>
                              <p className="text-xs text-gray-400 mt-1">{notif.tiempo}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-200">
                      <button className="w-full py-2 text-sm text-teal-600 font-medium hover:bg-teal-50 rounded-lg transition-colors cursor-pointer">
                        Ver todas las notificaciones
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings Button */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setShowConfig(!showConfig);
                    setShowNotifications(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-settings-3-line text-xl text-gray-600"></i>
                </button>

                {/* Settings Dropdown */}
                {showConfig && (
                  <div className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Configuración</h3>
                      <p className="text-sm text-gray-500 mt-1">Administra todas las opciones del sistema</p>
                    </div>
                    <div className="max-h-[480px] overflow-y-auto p-4">
                      {configuracionOpciones.map((categoria) => (
                        <div key={categoria.categoria} className="mb-4 last:mb-0">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            {categoria.categoria}
                          </p>
                          <div className="space-y-1">
                            {categoria.items.map((item) => (
                              <button
                                key={item.titulo}
                                onClick={() => handleConfigItemClick(item.id)}
                                className="w-full flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-left"
                              >
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                                  <i className={`${item.icon} text-teal-600 text-lg`}></i>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900">{item.titulo}</p>
                                  <p className="text-xs text-gray-500">{item.descripcion}</p>
                                </div>
                                <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Click outside to close dropdowns */}
        {(showNotifications || showConfig) && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => {
              setShowNotifications(false);
              setShowConfig(false);
            }}
          ></div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      {/* Configuration Modals */}
      {renderConfigModal()}

      {/* Modal de Gestión de Sucursales */}
      {showSucursalesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowSucursalesModal(false)}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-4xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Gestión de Sucursales</h3>
                    <p className="text-sm text-gray-500 mt-1">Administra todas las ubicaciones del gimnasio</p>
                  </div>
                  <button
                    onClick={handleOpenNewSucursal}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Nueva Sucursal
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sucursalesDB.map((sucursal) => (
                    <div key={sucursal.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                            <i className="ri-building-line text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">{sucursal.nombre}</h4>
                            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${
                              sucursal.estado === 'activo' ? 'bg-green-100 text-green-700' :
                              sucursal.estado === 'mantenimiento' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {sucursal.estado === 'activo' ? 'Activo' : 
                               sucursal.estado === 'mantenimiento' ? 'Mantenimiento' : 'Inactivo'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleEditSucursal(sucursal)}
                          className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                        >
                          <i className="ri-edit-line text-gray-600"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteSucursal(sucursal)}
                          disabled={deletingSucursalId === sucursal.id}
                          className="p-2 hover:bg-red-50 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Eliminar sucursal"
                        >
                          <i className="ri-delete-bin-line text-red-600"></i>
                        </button>
                      </div>

                      <div className="space-y-2 text-sm">
                        {sucursal.direccion && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-map-pin-line text-gray-400"></i>
                            <span>{sucursal.direccion}</span>
                          </div>
                        )}
                        {sucursal.telefono && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-phone-line text-gray-400"></i>
                            <span>{sucursal.telefono}</span>
                          </div>
                        )}
                        {sucursal.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <i className="ri-mail-line text-gray-400"></i>
                            <span>{sucursal.email}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500">Usuarios Activos</p>
                          <p className="text-lg font-semibold text-gray-900">{sucursal.usuarios_activos || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Capacidad Máxima</p>
                          <p className="text-lg font-semibold text-gray-900">{sucursal.capacidad_maxima || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => setShowSucursalesModal(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar/Nueva Sucursal */}
      {(showEditSucursalModal || showNewSucursalModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => {
                setShowEditSucursalModal(false);
                setShowNewSucursalModal(false);
                setSelectedSucursal(null);
                setSucursalForm(buildSucursalForm());
              }}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-3xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {showNewSucursalModal ? 'Nueva Sucursal' : 'Editar Sucursal'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {showNewSucursalModal ? 'Agrega una nueva ubicación' : 'Modifica la información de la sucursal'}
                </p>
              </div>

              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Sucursal</label>
                      <input
                        type="text"
                        value={sucursalForm.nombre}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Ej: PowerFit Centro"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                      <select
                        value={sucursalForm.estado}
                        onChange={(e) =>
                          setSucursalForm((prev) => ({
                            ...prev,
                            estado: e.target.value as 'activo' | 'mantenimiento' | 'inactivo',
                          }))
                        }
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="activo">Activo</option>
                        <option value="mantenimiento">Mantenimiento</option>
                        <option value="inactivo">Inactivo</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={sucursalForm.direccion}
                      onChange={(e) => setSucursalForm((prev) => ({ ...prev, direccion: e.target.value }))}
                      placeholder="Av. Principal 123, Santiago"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={sucursalForm.telefono}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, telefono: e.target.value }))}
                        placeholder="+56 9 8765 4321"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={sucursalForm.email}
                        onChange={(e) => setSucursalForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="sucursal@powerfit.cl"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Capacidad Máxima</label>
                    <input
                      type="number"
                      value={sucursalForm.capacidad_maxima}
                      onChange={(e) => setSucursalForm((prev) => ({ ...prev, capacidad_maxima: e.target.value }))}
                      min="50"
                      max="500"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Horarios de Atención</label>
                    <div className="space-y-3">
                      {DIAS_SEMANA.map((dia) => {
                        const horario = sucursalForm.horarios[dia];
                        return (
                          <div key={dia} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                            <div className="w-28">
                              <span className="text-sm font-medium text-gray-700 capitalize">{dia}</span>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!horario.cerrado}
                                onChange={(e) => handleHorarioSucursalChange(dia, { cerrado: !e.target.checked })}
                                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                              />
                              <span className="text-sm text-gray-600">Abierto</span>
                            </label>
                            <input
                              type="time"
                              value={horario.apertura}
                              onChange={(e) => handleHorarioSucursalChange(dia, { apertura: e.target.value })}
                              disabled={horario.cerrado}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                              type="time"
                              value={horario.cierre}
                              onChange={(e) => handleHorarioSucursalChange(dia, { cierre: e.target.value })}
                              disabled={horario.cerrado}
                              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => {
                    setShowEditSucursalModal(false);
                    setShowNewSucursalModal(false);
                    setSelectedSucursal(null);
                    setSucursalForm(buildSucursalForm());
                  }}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveSucursal}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-save-line mr-2"></i>
                  {showNewSucursalModal ? 'Crear Sucursal' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mi Perfil */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowProfileModal(false)}
            ></div>

            <div className="relative bg-white rounded-xl shadow-xl transform transition-all w-full max-w-2xl mx-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mi Perfil</h3>
                    <p className="text-sm text-gray-500 mt-1">Gestiona tu información personal</p>
                  </div>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-500"></i>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-4 border-b border-gray-200">
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setProfileTab('info');
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                      profileTab === 'info' 
                        ? 'border-teal-500 text-teal-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Información Personal
                  </button>
                  <button
                    onClick={() => {
                      setProfileTab('password');
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                    className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                      profileTab === 'password' 
                        ? 'border-teal-500 text-teal-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Cambiar Contraseña
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                {profileTab === 'info' ? (
                  <div className="space-y-6">
                    {/* Avatar con Drag & Drop */}
                    <div 
                      className={`flex items-center gap-6 p-4 rounded-xl border-2 border-dashed transition-all ${
                        isDraggingAvatar 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-300 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                      onDragOver={handleAvatarDragOver}
                      onDragLeave={handleAvatarDragLeave}
                      onDrop={handleAvatarDrop}
                    >
                      <div 
                        className={`w-24 h-24 rounded-full flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                          isDraggingAvatar ? 'ring-4 ring-teal-300 scale-105' : 'bg-teal-100'
                        }`}
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {profileData.avatar ? (
                          <img src={profileData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                        ) : isDraggingAvatar ? (
                          <i className="ri-download-2-line text-4xl text-teal-600"></i>
                        ) : (
                          <span className="text-4xl font-bold text-teal-600">
                            {profileData.nombre.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={avatarInputRef}
                          onChange={handleAvatarUpload}
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          className="hidden"
                        />
                        {isDraggingAvatar ? (
                          <div className="text-center">
                            <p className="text-sm font-medium text-teal-700">¡Suelta la imagen aquí!</p>
                            <p className="text-xs text-teal-600 mt-1">La imagen se cargará automáticamente</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-700 mb-1">Foto de Perfil</p>
                            <p className="text-xs text-gray-500 mb-3">Arrastra una imagen o usa los botones</p>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => avatarInputRef.current?.click()}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                              >
                                <i className="ri-upload-2-line mr-2"></i>
                                {profileData.avatar ? 'Cambiar Foto' : 'Subir Foto'}
                              </button>
                              {profileData.avatar && (
                                <button 
                                  onClick={() => setProfileData(prev => ({ ...prev, avatar: '' }))}
                                  className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                                >
                                  <i className="ri-delete-bin-line mr-2"></i>
                                  Eliminar
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">JPG, PNG o GIF. Máximo 2MB</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Información */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo</label>
                        <input
                          type="text"
                          value={profileData.nombre}
                          onChange={(e) => setProfileData(prev => ({ ...prev, nombre: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                        <input
                          type="tel"
                          value={profileData.telefono}
                          onChange={(e) => setProfileData(prev => ({ ...prev, telefono: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                        <input
                          type="text"
                          value={profileData.cargo}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                        />
                      </div>
                    </div>

                    {/* Info adicional */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Información de la Cuenta</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Fecha de Ingreso</p>
                          <p className="font-medium text-gray-900">
                            {new Date(profileData.fechaIngreso).toLocaleDateString('es-CL', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Rol</p>
                          <p className="font-medium text-gray-900">{profileData.cargo}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <i className="ri-shield-keyhole-line text-amber-600 text-xl mt-0.5"></i>
                        <div>
                      <p className="text-sm font-medium text-amber-800">Seguridad de la Contraseña</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Tu contraseña debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas y números.
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Modo temporal: se permite cambiar clave sin pedir la contraseña actual.
                          </p>
                        </div>
                      </div>
                    </div>

                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                        {passwordSuccess}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          placeholder="••••••••"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <i className="ri-lock-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Nueva Contraseña</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="••••••••"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <i className="ri-lock-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button 
                  onClick={() => setShowProfileModal(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  onClick={profileTab === 'info' ? handleSaveProfile : handleChangePassword}
                  disabled={profileTab === 'password' && passwordSaving}
                  className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-line mr-2"></i>
                  {profileTab === 'info' ? 'Guardar Cambios' : passwordSaving ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}







